import {
  flattenSearchCriteria,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import { unfoldRatingOnlyFilter } from 'features/charts-v2/utils/fold-rating-only-filter'
import {
  type AppliedFilterOption,
  type AppliedFilterOptionRange,
  type AppliedFilterOptionSelect,
} from 'features/filters/types'
import { type Filters, type SearchTermsLogical } from 'types/project'

// Sentiment buckets also carry min/max with no values; they keep their
// existing select-shape projection.
const SENTIMENT_RANGE_FIELDS = new Set(['sentiment', 'post_sentiment'])

/**
 * Converts a single Charts-2 filter option to v1 AppliedFilterOption shape.
 * Field identities remain native; the backend owns compatibility aliases.
 *
 * Returning `null` IS the definition of "this filter cannot be represented in
 * v1" (numeric filters — `record_count`/`sentence_count`/`sentiment_filter` —
 * always land here because they carry `numericValue` with empty `values`).
 * `translateV1FiltersToPanel` reuses this exact function to decide which prior
 * panel filters it must preserve rather than silently drop on the v1->v2 leg
 * of the round trip. Do not duplicate this predicate elsewhere.
 */
export const convertFilterOption = (
  filter: SelectedFilterOption,
): AppliedFilterOption | null => {
  // Window/range filters (score/post_score) carry their state in `min`/`max`/
  // `valueType`, NOT in `values`. They MUST translate to the v1 range shape
  // `{field, value_type, min, max}` — emitting the select shape `{field,
  // values:[]}` instead silently drops the reviews/ratings choice and the score
  // range, and the empty-values `score` 500s feedback's range-constraint
  // validator. Mirror summary-utils' buildFilterCriteria + the reverse
  // translator (translate-v1-filters-to-panel) which both use this same shape.
  if (
    (filter.field === 'score' || filter.field === 'post_score') &&
    (filter.min !== undefined ||
      filter.max !== undefined ||
      filter.valueType !== undefined)
  ) {
    return {
      field: filter.field,
      value_type: filter.valueType ?? 0,
      min: filter.min ?? 1,
      max: filter.max ?? 5,
      ...(filter.values.includes('Null') ? { values: ['Null'] } : {}),
    } as AppliedFilterOptionRange
  }

  // A numeric custom field's range (e.g. a price) has no v1 shape — v1's range
  // is the rating's. As the select shape it would become `{values: []}`, which
  // v1 reads as no filter and the round trip replaces, so it's unrepresentable.
  if (
    !SENTIMENT_RANGE_FIELDS.has(filter.field) &&
    filter.values.length === 0 &&
    (filter.min !== undefined || filter.max !== undefined)
  ) {
    return null
  }

  // Handle different operators
  switch (filter.operator) {
    case 'is':
      // Standard value filter
      return {
        field: filter.field,
        values: filter.values,
      } as AppliedFilterOptionSelect

    case 'is not':
      // V1 doesn't have a direct "is not" operator in the filter format
      // These are typically handled by the backend when processing filters
      // For export purposes, we'll include them as regular filters
      // The ExportDrawer components will handle them appropriately
      return {
        field: filter.field,
        values: filter.values,
      } as AppliedFilterOptionSelect

    default:
      // Fallback for unknown operators
      if (filter.values.length > 0) {
        return {
          field: filter.field,
          values: filter.values,
        } as AppliedFilterOptionSelect
      }
      return null
  }
}

/**
 * Builds search_terms_logical from panel.searchCriteria
 * Maps UI operator labels to backend logical operator format
 */
const buildSearchTermsLogical = (
  searchCriteria: Record<string, string[]>,
): SearchTermsLogical | null => {
  const entries = Object.entries(searchCriteria)
  if (entries.length === 0) {
    return null
  }

  const logical: SearchTermsLogical = {
    include_all: [],
    include_any: [],
    exclude_all: [],
    exclude_all_of: [],
  }

  for (const [op, values] of entries) {
    if (values.length === 0) continue
    if (op === 'Includes all') {
      logical.include_all!.push(...values)
    } else if (op === 'Includes some') {
      logical.include_any!.push(...values)
    } else if (op === 'Excludes all') {
      // NOT(AND) — drop only posts containing every term.
      logical.exclude_all_of!.push(...values)
    } else {
      // 'Excludes some' and any unknown operator → NOT(OR) default.
      logical.exclude_all!.push(...values)
    }
  }

  const hasTerms =
    logical.include_all!.length > 0 ||
    logical.include_any!.length > 0 ||
    logical.exclude_all!.length > 0 ||
    logical.exclude_all_of!.length > 0

  return hasTerms ? logical : null
}

/**
 * Converts Charts-2 panel to v1 Filters format
 * This is needed for the ExportDrawer which expects v1 format
 *
 * @param panel - Charts-2 panel object
 * @returns Filters object in v1 format
 */
export const translatePanelToV1Filters = (panel: ChartPanel): Filters => {
  const appliedFilters: AppliedFilterOption[] = []

  // 1. Add date range as a filter (create_time field)
  // Panel dateRange is already in Unix timestamps (seconds)
  if (panel.dateRange.startDate && panel.dateRange.endDate) {
    const dateFilter: AppliedFilterOptionRange = {
      field: 'create_time',
      value_type: 0, // 0 indicates range filter
      min: panel.dateRange.startDate,
      max: panel.dateRange.endDate,
    }
    appliedFilters.push(dateFilter)
  }

  // 2. Convert all selected filter options
  // A folded rating-only `valueType` has no v1 range; v1 says it exactly.
  unfoldRatingOnlyFilter(panel.filters).forEach((filter) => {
    const converted = convertFilterOption(filter)
    if (converted) {
      appliedFilters.push(converted)
    }
  })

  // 4. Build search query and condition arrays
  const searchQuery: string[] = []
  const searchCondition: string[] = []

  // Smart-search routing: v1 represents semantic search by setting
  // searchCondition to ["SEMANTIC"] (exclusive — no "body" entries). Mirror
  // that here so a v2 panel saved/loaded through the v1 wire format
  // round-trips correctly.
  const hasSmartSearchTerm = panel.searchTerms.some(
    (t) => t && t.trim().length > 0,
  )

  if (panel.smartSearchEnabled && hasSmartSearchTerm) {
    panel.searchTerms.forEach((term) => {
      if (term && term.trim()) {
        searchQuery.push(term.trim())
      }
    })
    searchCondition.push('SEMANTIC')
  } else {
    // Add search terms (non-semantic)
    panel.searchTerms.forEach((term) => {
      if (term && term.trim()) {
        searchQuery.push(term.trim())
        searchCondition.push('body') // Default to searching in body
      }
    })

    // Add search criteria (field-specific searches)
    const flattenedCriteria = flattenSearchCriteria(panel.searchCriteria)
    flattenedCriteria.forEach((field) => {
      if (field && field.trim()) {
        // Note: the original implementation had keyword values paired with fields
        // This simplified version just adds the field as criteria
        searchCondition.push(field.trim())
      }
    })
  }

  // Build search_terms_logical from searchCriteria to preserve operator semantics
  const searchTermsLogical = buildSearchTermsLogical(panel.searchCriteria)

  return {
    values: appliedFilters,
    searchQuery,
    searchCondition,
    search_terms_logical: searchTermsLogical,
    // Persist the time-bucket granularity alongside the date window so it
    // survives the cross-view V1-filter interchange (translateV1FiltersToPanel
    // reads it back). Without this the bucket resets to the preset default on
    // any panel rebuilt from V1 filters, while the date range persists.
    timeInterval: panel.timeBucket,
  }
}
